import { QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from '~/hooks/use-toast';
import { ApiError } from '~/types/api';

const errorsToIgnore = [ApiError.NoPreviousInstance];

/** Default freshness window for queries — keeps the cache warm without bombarding the API. */
const DEFAULT_STALE_TIME_MS = 60 * 1000;
/** How long an unsubscribed query lingers in cache before garbage collection. */
const DEFAULT_GC_TIME_MS = 5 * 60 * 1000;

/**
 * Creates the singleton {@link QueryClient} used by the app.
 *
 * Defaults are tuned for SystemZero's usage patterns:
 * - `staleTime` of 60s prevents stampedes from sibling components mounting at the same time.
 * - `gcTime` of 5m keeps recently-visited routes hot for snappy back-navigation.
 * - `refetchOnWindowFocus` is disabled because the app already polls actively-changing
 *   data via `refetchInterval`; window-focus refetch on top of that mostly produces noise.
 * - `retry` is disabled so failures surface immediately to the user via the global handler
 *   below; routes that genuinely benefit from retry can opt in per-query.
 *
 * The {@link QueryCache} `onError` shows a destructive toast for any `AxiosError` whose
 * server `reason` is not in `errorsToIgnore`. Individual queries can opt out by setting
 * `meta.skipGlobalErrorHandler: true`.
 */
export const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.skipGlobalErrorHandler) return;
        if (error instanceof AxiosError && error.response && !errorsToIgnore.includes(error.response.data.reason)) {
          toast({
            title: error.response.data.reason,
            description: error.response.data.message,
            variant: 'destructive',
          });
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  });
